import { LoadingButton } from "@mui/lab";
import { Alert, Box, CircularProgress, Link, Paper, Typography, useTheme } from "@mui/material";
import { packageVersion as webglApiVersion } from "@novorender/api";
import { useCallback, useEffect, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import { dataApi } from "apis/dataV1";
import { Events, track, trackBeacon } from "apis/procore";
import { useAppSelector } from "app/redux-store-interactions";
import { Accordion, AccordionDetails, AccordionSummary } from "components";
import { StorageKey } from "config/storage";
import { useGuidanceSnapshot } from "features/gpuDetection/useGuidanceSnapshot";
import { useSceneId } from "hooks/useSceneId";
import { selectUser } from "slices/authSlice";
import { selectConfig, selectGpuGuidanceEnabled, selectIsProcoreDesign, selectProcoreProjectId } from "slices/explorer";
import { telemetry } from "telemetry";
import { AsyncStatus } from "types/misc";
import { createOAuthStateString, generateCodeChallenge } from "utils/auth";
import { locationAssign, locationReload } from "utils/navigation";
import { deleteFromStorage, saveToStorage } from "utils/storage";

import { selectSceneStatus } from "../renderSlice";
import { ProcoreSceneError } from "./procoreSceneError";
import { ErrorKind } from "./types";

export function SceneError() {
    const { t } = useTranslation();
    const theme = useTheme();
    const sceneId = useSceneId();
    const status = useAppSelector(selectSceneStatus);
    const user = useAppSelector(selectUser);
    const config = useAppSelector(selectConfig);
    const isProcoreDesign = useAppSelector(selectIsProcoreDesign);
    const procoreProjectId = useAppSelector(selectProcoreProjectId);
    const [loading, setLoading] = useState(false);
    const redirect =
        !isProcoreDesign && status.status === AsyncStatus.Error && status.msg === ErrorKind.NOT_AUTHORIZED && !user;
    const gpuGuidanceEnabled = useAppSelector(selectGpuGuidanceEnabled);
    const guidanceSnapshot = useGuidanceSnapshot();
    const didRetryRef = useRef(false);

    // Novorender-only. Both callers are gated on `!isProcoreDesign`, because the Procore
    // card's sole action is Reload: re-authentication for a Procore-backed embed belongs
    // to the Procore host app, not to the Novorender auth server. That leaves a Procore-UX
    // embed on the Novorender backend with no re-auth path, which is accepted on the
    // assumption that combination is retired — see BM-14441.
    const loginRedirect = useCallback(
        async (forceLogin?: boolean) => {
            const tenant = await fetch(`${dataApi.serviceUrl}/api/scenes/${sceneId}`)
                .then((res) => res.json())
                .then((res) => ("tenant" in res ? res.tenant : undefined))
                .catch((_err) => undefined);

            const state = createOAuthStateString({
                sceneId,
                service: "self",
                query: window.location.search,
            });

            const [verifier, challenge] = await generateCodeChallenge();
            saveToStorage(StorageKey.CodeVerifier, verifier);

            const loginUrl =
                config.authServerUrl +
                `/auth` +
                "?response_type=code" +
                `&client_id=${config.novorenderClientId}` +
                `&redirect_uri=${window.location.origin}` +
                `&state=${state}` +
                `&code_challenge=${challenge}` +
                `&code_challenge_method=S256` +
                (tenant ? `&tenant_id=${tenant}` : "") +
                (isProcoreDesign ? `&prompt=Procore` : "");

            if (forceLogin) {
                locationAssign(`${config.authServerUrl}/signout?return_url=${encodeURIComponent(loginUrl)}`);
            } else {
                locationAssign(loginUrl);
            }
        },
        [sceneId, config, isProcoreDesign],
    );

    useEffect(
        function handleRedirect() {
            if (!redirect) {
                return;
            }

            setLoading(true);
            loginRedirect();
        },
        [redirect, sceneId, loginRedirect],
    );

    // ── Observability: track which ErrorKind leads to abandon vs. retry ─────────

    // Fired once when the error screen becomes visible.
    useEffect(
        function trackViewed() {
            if (status.status !== AsyncStatus.Error) {
                return;
            }
            const props = { error_kind: status.msg, has_user: !!user };
            void track(Events.sceneError.viewed, props);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [], // intentionally empty: run only on mount (error screen is only ever mounted once)
    );

    // Fired via `beforeunload` when the user closes/navigates away without retrying.
    useEffect(
        function trackAbandon() {
            if (status.status !== AsyncStatus.Error) {
                return;
            }
            const errorKind = status.msg;

            const handler = () => {
                if (didRetryRef.current) {
                    return;
                }
                const props = { error_kind: errorKind, has_user: !!user };
                trackBeacon(Events.sceneError.abandon, { ...props, recovery_action: "close_tab" });
                telemetry.recordErrorRecovery(errorKind as ErrorKind, "close_tab");
            };

            window.addEventListener("beforeunload", handler);
            return () => window.removeEventListener("beforeunload", handler);
        },
        [status, user],
    );

    const trackRetry = useCallback(
        (action: "refresh" | "switch_user" | "project_home") => {
            if (status.status !== AsyncStatus.Error) {
                return;
            }
            const props = { error_kind: status.msg, has_user: !!user, recovery_action: action };
            void track(Events.sceneError.retry, props);
            telemetry.recordErrorRecovery(status.msg as ErrorKind, action);
        },
        [status, user],
    );

    const switchUser = () => {
        didRetryRef.current = true;
        trackRetry(isProcoreDesign ? "refresh" : "switch_user");
        setLoading(true);
        deleteFromStorage(StorageKey.RefreshToken);
        loginRedirect(true);
    };

    const reloadViewer = useCallback(() => {
        didRetryRef.current = true;
        trackRetry("refresh");
        locationReload();
    }, [trackRetry]);

    // Both Procore card actions have to live here, next to `didRetryRef`: leaving for the
    // project home still fires `beforeunload`, so without setting the ref the user taking
    // the only action the card offers is recorded as having closed the tab.
    const goToProjectHome = useCallback(() => {
        if (procoreProjectId === undefined) {
            return;
        }
        didRetryRef.current = true;
        trackRetry("project_home");
        locationAssign(new URL(`/${procoreProjectId}/project/home`, window.location.origin).href);
    }, [procoreProjectId, trackRetry]);

    if (status.status !== AsyncStatus.Error) {
        return null;
    }

    const { msg: error, stack } = status;
    // GPU guidance deliberately wins over this card. A hard-stop already tells the user
    // their hardware cannot run the viewer, so showing an engine-failure card beside it
    // would offer a second, weaker explanation of the same failure. An INIT_CANVAS_ERROR
    // that guidance has not classified as a hard-stop still renders normally.
    const hideInitCanvasError =
        error === ErrorKind.INIT_CANVAS_ERROR &&
        gpuGuidanceEnabled &&
        guidanceSnapshot?.lifecycle.surface === "hard-stop";

    if (hideInitCanvasError) {
        return null;
    }

    if (isProcoreDesign) {
        return (
            <ProcoreSceneError
                error={error as ErrorKind}
                onReload={reloadViewer}
                onGoProjectHome={procoreProjectId === undefined ? undefined : goToProjectHome}
            />
        );
    }

    return (
        <Box
            bgcolor={theme.palette.secondary.main}
            display="flex"
            justifyContent="center"
            alignItems="center"
            height={"100vh"}
        >
            {redirect ? (
                <CircularProgress />
            ) : (
                <Paper sx={{ minWidth: 320, maxWidth: `min(600px, 90%)`, wordBreak: "break-word", p: 2 }}>
                    <Box>
                        <Typography paragraph variant="h4" component="h1" align="center">
                            {error === ErrorKind.NOT_AUTHORIZED
                                ? t("notAuthorized")
                                : error === ErrorKind.UNKNOWN_ERROR
                                  ? t("unknownError")
                                  : error === ErrorKind.OFFLINE_UNAVAILABLE
                                    ? t("offlineUnavailable")
                                    : error === ErrorKind.INVALID_SCENE
                                      ? t("invalidScene")
                                      : error === ErrorKind.DELETED_SCENE
                                        ? t("deletedScene")
                                        : t("unableToLoadScene")}
                        </Typography>
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            {error === ErrorKind.NOT_AUTHORIZED && user && (
                                <>
                                    {t("loggedInAs") + " "}
                                    <em>{user.user}</em> {t("noAccessToScene") + " "}
                                    <em>{sceneId}</em>.
                                </>
                            )}
                            {error === ErrorKind.EMPTY_PROJECT && <>{t("projectIsEmpty")}</>}
                            {error === ErrorKind.UNKNOWN_ERROR && (
                                <>
                                    {t("failedToDownloadTheScene")}
                                    <br />
                                    <br />
                                    {t("unsupportedBrowser")}
                                </>
                            )}
                            {error === ErrorKind.OFFLINE_UNAVAILABLE && (
                                <>
                                    {t("sceneWithId")} <em>{sceneId}</em> {t("notDownloaded")}
                                </>
                            )}
                            {error === ErrorKind.INVALID_SCENE && (
                                <>
                                    {t("sceneWithId")} <em>{sceneId}</em> {t("notExist")}
                                </>
                            )}
                            {error === ErrorKind.DELETED_SCENE && <>{t("sceneDeleted")}</>}
                            {error === ErrorKind.INIT_CANVAS_ERROR && (
                                <Trans i18nKey="initCanvasError">
                                    Error initializing 3D engine. Please check out the
                                    <Link
                                        href="https://support.novorender.com/en/articles/326399-white-screen-problems"
                                        target="_blank"
                                    >
                                        support page
                                    </Link>
                                    for possible solutions.
                                </Trans>
                            )}
                        </Alert>

                        {error === ErrorKind.NOT_AUTHORIZED && user ? (
                            <Box display={"flex"} justifyContent="center">
                                <LoadingButton
                                    onClick={() => {
                                        switchUser();
                                    }}
                                    sx={{ mt: 3, mb: 2, minWidth: 250 }}
                                    variant="contained"
                                    color="primary"
                                    size="large"
                                    loading={loading}
                                    loadingIndicator={
                                        <Box position={"relative"} display="flex" alignItems="center" minWidth={75}>
                                            {t(isProcoreDesign ? "refresh" : "switchUser")}
                                            <CircularProgress sx={{ ml: 1 }} color="inherit" size={16} />
                                        </Box>
                                    }
                                >
                                    {t(isProcoreDesign ? "refresh" : "switchUser")}
                                </LoadingButton>
                            </Box>
                        ) : (
                            <Accordion>
                                <AccordionSummary>{t("details")}</AccordionSummary>
                                <AccordionDetails>
                                    <Box p={1}>
                                        <>
                                            Timestamp: {new Date().toISOString()} <br />
                                            API: {webglApiVersion} <br />
                                            Dataserver: {(dataApi as { serviceUrl?: string })?.serviceUrl}
                                            {stack ? (
                                                <Box mt={2}>
                                                    ERROR: <br />
                                                    {stack}
                                                </Box>
                                            ) : null}
                                        </>
                                    </Box>
                                </AccordionDetails>
                            </Accordion>
                        )}
                    </Box>
                </Paper>
            )}
        </Box>
    );
}
